import type { Listing } from "@lumora/core";
import { appCover } from "../apps/appListings.js";
import { LANDING_WORK } from "./landingWork.js";

export interface HeroDeckItem {
  name: string;
  shotSrc: string;
  shotAlt: string;
  /** Stable sort / dedupe key from the site row when available. */
  slug?: string;
}

/**
 * Max queue length: slots 0–2 front fan, 3–6 greyed back stack (six work sites + apps).
 * Fewer unique sites simply fill fewer slots — never padded by repeating a
 * site, so each featured site appears exactly once per cycle.
 */
export const HERO_DECK_SIZE = 7;

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function sortKey(item: { slug?: string; name: string }): string {
  const slug = item.slug?.trim();
  if (slug) return normalizeKey(slug);
  return normalizeKey(item.name);
}

/**
 * Dedupe by slug (else name), sort by that key, cap at `size`. No repetition
 * padding: cycle length equals the number of unique sites. A further featured site from the API joins automatically —
 * no hardcoded marketing order list.
 */
export function heroDeckFromFeatured(
  items: readonly { name: string; shotSrc: string; shotAlt: string; slug?: string }[],
  size = HERO_DECK_SIZE,
): HeroDeckItem[] {
  const byKey = new Map<string, HeroDeckItem>();
  for (const item of items) {
    if (!item.name?.trim() || !item.shotSrc) continue;
    const key = sortKey(item);
    if (byKey.has(key)) continue;
    const card: HeroDeckItem = {
      name: item.name.trim(),
      shotSrc: item.shotSrc,
      shotAlt: item.shotAlt || item.name,
    };
    const slug = item.slug?.trim();
    if (slug) card.slug = slug;
    byKey.set(key, card);
  }

  if (byKey.size === 0) return HERO_DECK_ITEMS;

  const ordered = [...byKey.values()].sort((a, b) =>
    sortKey(a).localeCompare(sortKey(b), "en"),
  );

  return ordered.slice(0, size);
}

/** App listings as hero-deck windows (shown in the deck only, never in "Our work"). */
export function heroAppItems(listings: readonly Listing[]): HeroDeckItem[] {
  return listings.map((l) => ({
    name: l.title,
    shotSrc: appCover(l),
    shotAlt: `${l.title} app`,
    slug: l.id,
  }));
}

/** Static fallback: LANDING_WORK only (apps come from the API at runtime). */
export const HERO_DECK_ITEMS: HeroDeckItem[] = heroDeckFromFeatured(
  LANDING_WORK.map((w): HeroDeckItem => ({
    name: w.name,
    shotSrc: w.shotSrc,
    shotAlt: w.shotAlt,
    slug: w.slug,
  })),
);

export const HERO_DECK_COUNT = HERO_DECK_ITEMS.length;

/**
 * Position in the deck after shuffle: 0–2 = front fan, 3+ = back stack.
 * Advancing phase sends the front-left card to the bottom of the stack.
 */
export function heroDeckStackOffset(cardIndex: number, phase: number, count = HERO_DECK_COUNT): number {
  const n = count > 0 ? count : HERO_DECK_COUNT;
  return (cardIndex - phase + n) % n;
}

/** Stable per-card Z twist (±5° max) for back-stack cards. */
export function heroDeckStackTwistDeg(cardIndex: number): number {
  const twists = [4, -3, 5, -5, 2, -4, -2];
  return twists[cardIndex % twists.length] ?? 0;
}

/** Small stable XY jitter (px) for back-stack cards. */
export function heroDeckStackJitterPx(cardIndex: number): { x: number; y: number } {
  const jitters = [
    { x: 7, y: -5 },
    { x: -9, y: 4 },
    { x: 5, y: 6 },
    { x: -6, y: -3 },
    { x: 10, y: 2 },
    { x: -4, y: -7 },
    { x: 3, y: 5 },
  ];
  return jitters[cardIndex % jitters.length] ?? { x: 0, y: 0 };
}

/** Grayscale fill for back-stack cards from queue index (0 = lightest … darkest). */
export function heroDeckQueueGray(cardIndex: number, count = HERO_DECK_SIZE): string {
  const maxL = 0x58;
  const minL = 0x14;
  const t = cardIndex / Math.max(1, count - 1);
  const v = Math.round(maxL + (minL - maxL) * t);
  const hex = v.toString(16).padStart(2, "0");
  return `#${hex}${hex}${hex}`;
}
